import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { BOX_TRUCK_ASSET_DOC_LABELS, BOX_TRUCK_ASSET_DOC_TYPES } from '@numeo/types-core'
import type { BoxTruckAssetDocumentDto } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import { log } from '@/utils/logger'
import { ProfileDocuments } from './ProfileDocuments'
import { fetchBoxTruckAssetDocuments, removeBoxTruckAssetDocument, uploadBoxTruckAssetDocument } from '../services/boxTruckProfilesApi'
import type { BoxTruckPendingAssetDocumentUploads } from '../services/boxTruckProfilesApi'

interface BoxTruckAssetDocumentsProps {
    /** Absent on the create form — the vehicle has no id yet, so files are held rather than uploaded. */
    assetId?: string
    pendingDocuments?: BoxTruckPendingAssetDocumentUploads
    onPendingDocumentsChange?: Dispatch<SetStateAction<BoxTruckPendingAssetDocumentUploads>>
}

/**
 * A vehicle's documents, on both sides of its existence.
 *
 * With an `assetId` this is the stored set: read from the server, each upload sent as it is chosen. Without
 * one — the Add a New Truck form — there is nothing to read and nowhere to send, so the files are held in
 * the caller's state and uploaded once the truck has been created. Collecting them at creation is the point:
 * a registration and an insurance certificate are what the operator has in hand while they are typing the
 * plate, and making them come back through Edit afterwards is how vehicles end up without either.
 */
export const BoxTruckAssetDocuments = ({ assetId, pendingDocuments, onPendingDocumentsChange }: BoxTruckAssetDocumentsProps) => {
    const [documents, setDocuments] = useState<BoxTruckAssetDocumentDto[]>([])
    const [loading, setLoading] = useState(Boolean(assetId))
    const [loadedAssetId, setLoadedAssetId] = useState<string | undefined>(undefined)
    const [error, setError] = useState<{ assetId: string; message: string } | null>(null)
    const [refreshing, setRefreshing] = useState(false)
    const currentAssetId = useRef(assetId)
    currentAssetId.current = assetId

    useEffect(() => {
        if (!assetId) {
            // Nothing stored to show, and nothing in flight — the pending list below is the whole picture.
            setDocuments([])
            setLoadedAssetId(undefined)
            setLoading(false)
            setError(null)
            setRefreshing(false)
            return
        }
        let active = true
        setLoading(true)
        setError(null)
        setDocuments([])
        setRefreshing(false)
        void fetchBoxTruckAssetDocuments(assetId)
            .then((data) => {
                if (active) {
                    setDocuments(data)
                    setLoadedAssetId(assetId)
                }
            })
            .catch((cause: unknown) => {
                if (!active) return
                log.e('[box-truck] vehicle documents could not be loaded', cause, { assetId })
                setError({ assetId, message: 'Vehicle documents could not be loaded. Refresh to see the current files.' })
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [assetId])

    const refreshDocuments = async (): Promise<boolean> => {
        if (!assetId) return true
        setRefreshing(true)
        try {
            const refreshed = await fetchBoxTruckAssetDocuments(assetId)
            if (currentAssetId.current !== assetId) return true
            setDocuments(refreshed)
            setLoadedAssetId(assetId)
            setError(null)
            return true
        } catch (cause) {
            if (currentAssetId.current !== assetId) return true
            log.e('[box-truck] vehicle document status could not be refreshed', cause, { assetId })
            setError({ assetId, message: 'Could not confirm vehicle document status. The list may be out of date; refresh it before retrying removal.' })
            return false
        } finally {
            if (currentAssetId.current === assetId) setRefreshing(false)
        }
    }

    const loaded = loadedAssetId === assetId
    const visibleError = error && error.assetId === assetId ? error.message : null
    const hasPendingDocuments = Object.values(pendingDocuments ?? {}).some((files) => files && files.length > 0)
    if (loading || (!loaded && !visibleError)) return <p className="py-3 text-sm text-foreground-muted">Loading vehicle documents…</p>

    return (
        <div className="space-y-3">
            {visibleError && (
                <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl bg-surface p-3 shadow-surface">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
                    <p className="min-w-0 flex-1 text-sm text-foreground">{visibleError}</p>
                    <Button type="button" variant="dangerSoft" size="sm" disabled={refreshing} onClick={() => void refreshDocuments()}>
                        {refreshing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                        Refresh documents
                    </Button>
                </div>
            )}
            {(loaded || hasPendingDocuments) && (
                <ProfileDocuments
                    entityId={assetId}
                    types={BOX_TRUCK_ASSET_DOC_TYPES}
                    labels={BOX_TRUCK_ASSET_DOC_LABELS}
                    documents={documents}
                    onDocumentsChange={(update) => {
                        if (currentAssetId.current === assetId) setDocuments(update)
                    }}
                    pendingDocuments={pendingDocuments}
                    onPendingDocumentsChange={onPendingDocumentsChange}
                    entity="assets"
                    uploadDocument={uploadBoxTruckAssetDocument}
                    removeDocument={removeBoxTruckAssetDocument}
                    onRemovalFailure={async () => {
                        if (!(await refreshDocuments())) throw new Error('Could not refresh vehicle document status')
                    }}
                    removalEntityType="vehicle"
                    dropzoneCards
                    clickToChoose
                />
            )}
        </div>
    )
}
